import { Tabs, Switch, Button, Alert, Tooltip, Popover, Modal, List } from 'antd';
import './Trips.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import StopTimes from './StopTimes';
import { cloneDeep, find, last, noop, uniq, uniqBy, values } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useAppContext } from '../libs/contextLib';
import TimeField from '../libs/timeField';
import dayjs from '../dayjs';
import { dayjsToSecsSinceMidnight } from '../model/timeFilter';
import { BusRoute } from '../model/busRoute';
import { Tooltip as AntdTooltip } from 'antd';
import { keyBy } from 'lodash/collection';
import TripDetailsModal from '../components/TripDetailsModal';
import { driverModelData, vehicleModelData, shiftBatModelData } from '../services/ModelService';
import CalendarSelector from './CalendarSelector';
import { ReactComponent as Calendar } from '../assets/icons/Calendar.svg';
import SimpleStopTimes from './SimpleStopTimes';
import { ShiftBatRowType } from '../model/shiftBat';
import { detectDutyOverlaps } from '../utils/dutyUtils';
import { Warning, Trash } from './Icons';
import { ExclamationCircleOutlined, DeleteOutlined, MenuOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';


import { Popconfirm } from 'antd';
import { ReactComponent as Accessible } from '../assets/icons/Accessible.svg';
import { ReactComponent as School } from '../assets/icons/School.svg';


const { TabPane } = Tabs;



function Trips({
    route, setRoute, setRouteNow, allStops, schedules, setFocusStop, setSelectedStop,
    selectedStop, selectedScheduleId, setUpdated = noop, immutable = false,
    operator, setSelectedTripId, validationHelper = { global: [] },
    focusTransfers, setFocusTransfers, showSeconds, selectedTripId, buildToStop,
    cumulativeUpdate, setShowNewStop, showNewStop, showAllStops, editingRoute, charter = false,
    recurring = false, useSimple = false, setAllStops = noop, impactViewMode, setImpactViewMode,
    serviceModalVisible, setServiceModalVisible
}) {

    const { isAdmin } = useAppContext();
    // const [selectedTripId, setSelectedTripId] = useState(null);
    // eslint-disable-next-line
    const [vehicles, setVehicles] = useState([]);
    // eslint-disable-next-line
    const [drivers, setDrivers] = useState([]);
    const [allShiftBats, setAllShiftBats] = useState(null);
    const [servicesPopoverVisible, setServicesPopoverVisible] = useState(false);

    const schedulesById = keyBy(schedules, 'scheduleId');

    // Load shiftbats
    useEffect(() => {
        const loadShiftBats = async () => {
            try {
                const shiftBats = await shiftBatModelData.getAll();
                setAllShiftBats(shiftBats);
            } catch (error) {
                console.error('Failed to load shiftbats:', error);
            }
        };
        loadShiftBats();
    }, []);

    // Get driver duties for a specific trip
    const getDriverDutiesForTrip = useCallback((tripId) => {
        if (!allShiftBats || !tripId) return [];

        return values(allShiftBats).filter(shiftBat =>
            shiftBat.rows?.some(row =>
                row.type === ShiftBatRowType.service &&
                row.tripId === tripId
            )
        );
    }, [allShiftBats]);

    const isTripInvalidDueToDuties = useCallback((tripId) => {
        const duties = getDriverDutiesForTrip(tripId);
        if (!duties.length) return true;
        for (const duty of duties) {
            const { overlappingRowIds } = detectDutyOverlaps(duty.rows || []);
            if (overlappingRowIds.size > 0) return true;
        }
        return false;
    }, [getDriverDutiesForTrip]);

    const setTrip = useCallback((trip, now, opts) => {
        route = route.clone();
        const tripIdx = route.services.findIndex(t => t.tripId === trip.tripId);
        route.services[tripIdx] = trip;
        route = new BusRoute(route);
        route.setBaseStops(allStops, cloneDeep(trip.stopTimes), opts);

        if (now) {
            setRouteNow(route);
        } else {
            setRoute(route);
        }
    }, [setRoute, setRouteNow, allStops, route]);

    // const toggleRecurring = () => {
    //     setRecurring(!recurring);
    // };

    // Only show the selected trip (default to first)
    const selectedTrip = useMemo(() => route?.services?.find(t => t.tripId === selectedTripId) || route?.services?.[0], [route?.services, selectedTripId]);

    // Handler for selecting a trip from the popover
    const handleSelectTrip = (tripId) => {
        setSelectedTripId(tripId);
    };

    const otherTrips = useMemo(() => route?.services?.filter(t => t.tripId !== selectedTrip?.tripId) || [], [route?.services, selectedTrip?.tripId]);

    // Check if scheduleIds differ between trips
    const hasDifferentCalendars = useMemo(() => {
        if (!otherTrips?.length) return false;
        return otherTrips.some(trip => {
            if (!selectedTrip?.scheduleIds || !trip.scheduleIds) return true;
            if (selectedTrip.scheduleIds.length !== trip.scheduleIds.length) return true;
            const selectedSet = new Set(selectedTrip.scheduleIds);
            for (const id of trip.scheduleIds) {
                if (!selectedSet.has(id)) return true;
            }
            return false;
        });
    }, [selectedTrip?.scheduleIds, otherTrips]);

    const copyCalendars = useCallback(() => {
        if (!route) return;
        const updatedRoute = route.clone();
        otherTrips.forEach(trip => {
            const tripIdx = updatedRoute.services.findIndex(t => t.tripId === trip.tripId);
            if (tripIdx !== -1) {
                const existing = updatedRoute.services[tripIdx].scheduleIds || [];
                updatedRoute.services[tripIdx].scheduleIds = uniq([...existing, ...(selectedTrip.scheduleIds || [])]);
            }
        });
        setRoute(updatedRoute);
    }, [selectedTrip?.scheduleIds, route, setRoute, otherTrips]);

    const getTabItems = useCallback(() => route.services?.filter(trip => !selectedScheduleId || trip.scheduleIds.includes(selectedScheduleId))
        .map(trip => {
                const obsoleteTrip = trip.hasObsoleteCalendar(schedulesById);
                const tripInvalid = (validationHelper.isTripInvalid && validationHelper.isTripInvalid(trip.tripId)) || obsoleteTrip || isTripInvalidDueToDuties(trip.tripId);
                const duties = getDriverDutiesForTrip(trip.tripId);
                const hasDutyOverlap = duties.some(duty => {
                    const {overlappingRowIds} = detectDutyOverlaps(duty.rows || []);
                    return overlappingRowIds.size > 0;
                });
                return {
                    key: trip.tripId,
                    closable: useSimple,
                    label: trip.stopTimes.filter(s => s.stopId)?.length ? <Tooltip
                        title={(() => {
                            const tripMsgs = getTripValidationMessages(validationHelper, trip, selectedTrip, getDriverDutiesForTrip, isTripInvalidDueToDuties);
                            if (tripInvalid && tripMsgs.length) {
                                return <div>{tripMsgs.map((msg, i) => <div key={i}>{msg}</div>)}</div>;
                            }
                            return 'No validation issues';
                        })()}
                    >
                        <TimeField
                            value={trip.getStartTime(showSeconds)}
                            disabled={!trip.stopTimes?.length}
                            style={{width: showSeconds ? 58 : 48}} showSeconds={showSeconds}
                            className={`ant-input ant-input-sm inputtext-small text-center ${tripInvalid ? 'error' : ''}`}
                            onChange={(e, value) => {
                                if (!value) {
                                    return;
                                }
                                value = dayjs(value, showSeconds ? 'HH:mm:ss' : 'HH:mm');
                                if (!value.isValid()) {
                                    return;
                                }
                                const newStartSecs = dayjsToSecsSinceMidnight(value);
                                if (trip.stopTimes?.length) {
                                    trip.setStartTime(newStartSecs);
                                    route = route.clone();
                                    const tripIdx = route.services.findIndex(t => t.tripId === trip.tripId);
                                    route.services[tripIdx] = trip;
                                    route = new BusRoute(route);
                                    route.updateStops();
                                    setRouteNow(route);
                                }
                            }}/></Tooltip> : <Tooltip title="Please add stops to set the start time.">00:00</Tooltip>,
                    children: <> {!immutable && <>
                        {!useSimple ? <>
                            {process.env.REACT_APP_VERBOSE &&
                                <Row>
                                    <Col lg={12}><span
                                        className="font-weight-bolder small">Trip ID: {trip.tripId}</span></Col>
                                </Row>
                            }
                            <Row className="mb-1">
                                <Col className="pr-1">
                                    <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px'}}>
                                        <span className="info-heading">Operating {recurring ? 'Calendars' : 'Dates'}</span>
                                    </div>
                                    <CalendarSelector obj={trip} obsolete={obsoleteTrip} route={route}
                                                    setRoute={setRoute} schedules={schedules} recurring={recurring}
                                                    status={validationHelper?.global?.some(m => m.type === 'schedule' && m.tripId === trip.tripId) || obsoleteTrip ? 'error' : ''}
                                                    onCalendarChange={scheduleIds => {
                                                        trip.scheduleIds = scheduleIds;
                                                        setRoute(route.clone());
                                                    }}
                                                    onDatesChange={dates => {

                                                        trip.dates = dates;
                                                        setRouteNow(route.clone());
                                                    }}
                                                    charter={charter}
                                                    hidePublicCalendars
                                    />
                                </Col>
                                <Col className="pl-1 pr-1" style={{maxWidth: '200px'}}>
                                    <div style={{display: 'flex', flexDirection: 'row', gap: '8px'}}>
                                        <span className="info-heading">Driver Duties</span>
                                        {hasDutyOverlap &&
                                            <Tooltip
                                                title={hasDutyOverlap ? 'One or more duties have overlapping segments. Click to inspect.' : ''}
                                                open={hasDutyOverlap ? undefined : false}
                                            >
                                                <Warning style={{width: '14px', marginTop: '-2px', marginBottom: '2px'}} />
                                            </Tooltip>
                                        }
                                    </div>
                                    {duties.length > 3 ? (
                                        <Popover
                                            content={
                                                <div style={{maxWidth: '260px', maxHeight: '200px', overflowY: 'auto'}}>
                                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                                        {duties.map((shiftBat) => (
                                                            <div
                                                                key={shiftBat.shiftBatId}
                                                                className="RouteNumber-sm"
                                                                style={{backgroundColor: shiftBat.shiftBatColour || '#007bff',}}
                                                            >
                                                                {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            }
                                            title="All Driver Duties"
                                            trigger="hover"
                                            placement="bottom"
                                        >
                                            <div
                                                style={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    gap: '2px',
                                                    flexWrap: 'wrap',
                                                }}
                                                onClick={() => setImpactViewMode(v => !v)}
                                                className={impactViewMode ? 'active' : ''}
                                            >
                                                {duties.slice(0, 3).map((shiftBat, index) => (
                                                    <div
                                                        key={shiftBat.shiftBatId}
                                                        className="RouteNumber-sm"
                                                        style={{
                                                            minWidth: 'auto',
                                                            backgroundColor: shiftBat.shiftBatColour || '#007bff'
                                                        }}
                                                    >
                                                        {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                    </div>
                                                ))}
                                                <div
                                                    className="RouteNumber-sm"
                                                    style={{
                                                        minWidth: 'auto',
                                                        backgroundColor: '#E5E8E5',
                                                        color: '#777',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    +{duties.length - 3} more
                                                </div>
                                            </div>
                                        </Popover>
                                    ) : (
                                        <div
                                            style={{
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                gap: '2px',
                                                flexWrap: 'wrap',
                                            }}
                                            onClick={() => setImpactViewMode(v => !v)}
                                            className={impactViewMode ? 'active' : ''}
                                        >
                                            {duties.map((shiftBat, index) => (
                                                <div
                                                    key={shiftBat.shiftBatId}
                                                    className="RouteNumber-sm"
                                                    style={{
                                                        minWidth: 'auto',
                                                        backgroundColor: shiftBat.shiftBatColour || '#007bff'
                                                    }}
                                                >
                                                    {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                </div>
                                            ))}
                                            {duties.length === 0 && (
                                                <Alert message="No duties" type="error" style={{width: '100%', padding: '4px 10px', borderRadius: '6px'}} />
                                            )}
                                        </div>
                                    )}
                                </Col>
                                {/* <Col xs="auto" className="pl-1 pr-1">
                                    <Switch size="small" 
                                    // onChange={setRecurring}
                                    checked={recurring} style={{display: 'none'}} />
                                    <Tooltip placement="bottom" title={`${recurring ? 'Operating date' : 'Operating calendar'}`}>
                                        <Button 
                                        //onClick={toggleRecurring} 
                                        className={`info-icon-button ${recurring ? 'active' : ''}`}><Calendar /></Button>
                                    </Tooltip>
                                </Col> */}
                                <Col xs="auto" className="pl-1" style={{paddingTop: '14px'}}>
                                    <TripDetailsModal popPlacement={'top'} route={route}
                                                    trip={route.services ? find(route.services, {tripId: selectedTripId}) : null}
                                                    operator={operator} setTrip={setTrip}
                                                    allStops={allStops} schedules={schedules}
                                                    validationHelper={validationHelper}
                                                    setRoute={setRoute} setUpdated={setUpdated}
                                    />
                                </Col>
                            </Row>
                        </> : <></>}

                        {/*<Row>*/}
                        {/*    <Col lg={6}><span*/}
                        {/*        className='font-weight-bolder small'>Vehicle</span></Col>*/}
                        {/*    <Col lg={6}><span className='font-weight-bolder small'>Driver</span></Col>*/}
                        {/*</Row>*/}
                        {/*<Row className="mb-3">*/}
                        {/*    <Col lg={6}>*/}
                        {/*        <div>*/}
                        {/*            <Select*/}
                        {/*                style={{width: "100%"}}*/}
                        {/*                placeholder="Select a vehicle"*/}
                        {/*                onChange={(value) => {*/}
                        {/*                    trip.vehicleId = value*/}
                        {/*                    setRoute(route.clone())*/}
                        {/*                }}*/}
                        {/*                value={trip.vehicleId}*/}
                        {/*                // mode="multiple"*/}
                        {/*                // allowClear*/}
                        {/*                options={vehicles}*/}
                        {/*            />*/}
                        {/*        </div>*/}
                        {/*    </Col>*/}
                        {/*    <Col lg={6}>*/}
                        {/*        <div>*/}
                        {/*            <Select*/}
                        {/*                style={{width: "100%"}}*/}
                        {/*                placeholder="Select a driver"*/}
                        {/*                onChange={(value) => {*/}
                        {/*                    trip.driverId = value*/}
                        {/*                    setRoute(route.clone())*/}
                        {/*                }}*/}
                        {/*                value={trip.driverId}*/}
                        {/*                // mode="multiple"*/}
                        {/*                // allowClear*/}
                        {/*                options={drivers}*/}
                        {/*            />*/}
                        {/*        </div>*/}
                        {/*    </Col>*/}
                        {/*</Row>*/}
                    </>}
                        <Row className="TripsWrap">
                            <Col lg={12} className="h-100">
                                {useSimple?
                                    <SimpleStopTimes
                                      charter={charter}
                                      route={route}
                                      setTrip={setTrip}
                                      buildToStop={buildToStop}
                                      showNewStop={showNewStop}
                                      showAllStops={showAllStops}
                                      editingRoute={editingRoute}
                                      setShowNewStop={setShowNewStop}
                                      showSeconds={showSeconds}
                                      cumulativeUpdate={cumulativeUpdate}
                                      setRoute={setRoute}
                                      setRouteNow={setRouteNow}
                                      operator={operator}
                                      immutable={immutable}
                                      allStops={allStops}
                                      trip={trip.clone()}
                                      setFocusStop={setFocusStop}
                                      setSelectedStop={setSelectedStop}
                                      selectedStop={selectedStop}
                                      setUpdated={setUpdated}
                                      validationHelper={validationHelper}
                                      setFocusTransfers={setFocusTransfers}
                                      focusTransfers={focusTransfers}
                                      setAllStops={setAllStops}
                                    />
                                :
                                <StopTimes
                                    charter={charter}
                                    route={route}
                                    setTrip={setTrip}
                                    buildToStop={buildToStop}
                                    showNewStop={showNewStop}
                                    showAllStops={showAllStops}
                                    editingRoute={editingRoute}
                                    setShowNewStop={setShowNewStop}
                                    showSeconds={showSeconds}
                                    cumulativeUpdate={cumulativeUpdate}
                                    setRoute={setRoute}
                                    operator={operator}
                                    immutable={immutable}
                                    allStops={allStops}
                                    trip={trip.clone()}
                                    setFocusStop={setFocusStop}
                                    setSelectedStop={setSelectedStop}
                                    selectedStop={selectedStop}
                                    setUpdated={setUpdated}
                                    validationHelper={validationHelper}
                                    setFocusTransfers={setFocusTransfers}
                                    focusTransfers={focusTransfers}
                                />
                                }
                            </Col>
                        </Row></>
                };
            }
        ), [route.services]);

    useEffect(() => {

        const load = async () => {
            const vehicles = await vehicleModelData.getAll();
            setVehicles(values(vehicles).map(v => ({ label: v.vehicleName, value: v.vehicleId })));
            const drivers = values(await driverModelData.getAll());
            setDrivers(drivers.map(v => ({ label: v.driverName, value: v.driverId })));
        };

        load().then(() => console.log('drivers/vehicles loaded'));

    }, [setVehicles, setDrivers]);

    const handleNewTimetable = (selected, action) => {
        if (action === 'add') {
            const trip = route.addTrip();
            setRoute(route.clone());
            setSelectedTripId(trip.tripId);
            setUpdated(true);
        } else if (action === 'remove') {
            const trip = route.services.find(t => t.tripId === selected);
            route.removeTrip(trip.tripId);
            const lastTrip = last(route.services);
            setSelectedTripId(lastTrip?.tripId || null);
            setRoute(route.clone());
            setUpdated(true);
        }
    }

    const handleAddTrip = () => {
        const trip = route.addTrip();
        setRoute(route.clone());
        setSelectedTripId(trip.tripId);
        setUpdated(true);
    }

    // Calculate duty overlap for the selected trip
    const selectedTripDuties = selectedTrip ? (getDriverDutiesForTrip ? getDriverDutiesForTrip(selectedTrip.tripId) : []) : [];
    const hasDutyOverlap = selectedTripDuties.some(duty => {
        const { overlappingRowIds } = detectDutyOverlaps(duty.rows || []);
        return overlappingRowIds.size > 0;
    });

    const anyInvalidServices = useMemo(() => {
        const messages = []
        route.services.map(service => {
            if(route.charter) {
                messages.push(...getTripValidationMessages(validationHelper, service, service));
            } else {
                messages.push(...getTripValidationMessages(validationHelper, service, service, getDriverDutiesForTrip, isTripInvalidDueToDuties));
            }
        });
        return messages.length > 0;
    }, [route.services, validationHelper]);

    // Get validation messages for the currently selected service when anyInvalidServices is true
    const getSelectedServiceValidationMessages = useMemo(() => {
        if (!anyInvalidServices || !selectedTrip) return [];
        
        const messages = route.charter ? getTripValidationMessages(validationHelper, selectedTrip, selectedTrip) : getTripValidationMessages(validationHelper, selectedTrip, selectedTrip, getDriverDutiesForTrip, isTripInvalidDueToDuties);
        const uniqueMessages = uniqBy(messages, 'msg');
        return uniqueMessages;
    }, [anyInvalidServices, selectedTrip, validationHelper, getDriverDutiesForTrip, isTripInvalidDueToDuties]);

    return (
        <div className="col-lg-12 h-100">
            <div className="TimetableSchedule h-100">
                <Row className="h-100">
                    <Col lg={12} className={'p-0 h-100'}>
                        {/* Service selector header: summary on left, button on right */}
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2, width: '100%' }}>
                            {selectedTrip ? (
                                <>
                                    <Row style={{borderBottom: '1px solid #E9E5FE', paddingBottom: 8, width: 'calc(100% + 30px)'}}>
                                        <Col xs={12} xl={2} className='d-flex flex-column pr-0'>
                                            <span className="info-heading">Service</span>
                                            <Popover
                                                content={
                                                    <div style={{ maxWidth: '300px', maxHeight: '400px', overflowY: 'auto' }}>
                                                        <div>
                                                            <List
                                                                size="small"
                                                                dataSource={route.services}
                                                                renderItem={trip => {
                                                                    const isSelected = trip.tripId === selectedTripId;
                                                                    const numStops = trip.stopTimes ? trip.stopTimes.length : 0;
                                                                    const calendar = trip.scheduleIds && trip.scheduleIds.length > 0 ? trip.scheduleIds.join(', ') : 'No calendar';
                                                                    // Validation: check for obsolete, invalid, or duty overlap
                                                                    const obsolete = trip.hasObsoleteCalendar && trip.hasObsoleteCalendar(schedulesById);
                                                                    const tripInvalid = (validationHelper.isTripInvalid && validationHelper.isTripInvalid(trip.tripId)) || obsolete || (getDriverDutiesForTrip(trip.tripId) && isTripInvalidDueToDuties(trip.tripId));
                                                                    const tripMsgs2 = route.charter ? getTripValidationMessages(validationHelper, trip, selectedTrip) : getTripValidationMessages(validationHelper, trip, selectedTrip, getDriverDutiesForTrip, isTripInvalidDueToDuties);
                                                                    return (
                                                                        <div key={trip.tripId} style={{
                                                                            cursor: 'pointer',
                                                                            background: isSelected ? '#E8E6FA' : undefined,
                                                                            border: isSelected ? '1px solid #6B54F9' : '1px solid #D9D9D9',
                                                                            borderRadius: 6,
                                                                            marginBottom: 6,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            padding: '8px 10px',
                                                                            transition: 'background 0.2s, border 0.2s',
                                                                            gap: 8,
                                                                        }}
                                                                        onClick={() => handleSelectTrip(trip.tripId)}
                                                                        >
                                                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                                                <span style={{ fontWeight: 500, color: isSelected ? '#6B54F9' : '#333', fontSize: '13px', lineHeight: '15px' }}>
                                                                                    {trip.getStartTime ? trip.getStartTime(showSeconds) : ''}
                                                                                </span>
                                                                                <span style={{ fontSize: 12, color: isSelected ? '#555' : '#888', lineHeight: '12px' }}>
                                                                                    Stops: <span style={{ fontWeight: 500 }}>{numStops}</span>
                                                                                    {/* | {calendar} */}
                                                                                </span>
                                                                            </div>
                                                                            <Tooltip title={(() => {
                                                                                if (tripInvalid && tripMsgs2.length) {
                                                                                    return <div>{tripMsgs2.map((msg, i) => <div key={i}>{msg}</div>)}</div>;
                                                                                }
                                                                            })()}>
                                                                                {tripInvalid && tripMsgs2.length > 0 && <Warning style={{ width: '14px' }} />}
                                                                            </Tooltip>
                                                                            {/* <Button
                                                                                type={isSelected ? 'primary' : 'default'}
                                                                                size="small"
                                                                                style={{ marginLeft: 8, fontSize: '11px', padding: '2px 8px' }}
                                                                                onClick={e => {
                                                                                    e.stopPropagation();
                                                                                    handleSelectTrip(trip.tripId);
                                                                                }}
                                                                            >Go</Button> */}
                                                                            <Popconfirm
                                                                                title="Delete this service?"
                                                                                okText="Delete"
                                                                                cancelText="Cancel"
                                                                                onConfirm={e => {
                                                                                    e?.stopPropagation();
                                                                                    // Remove the trip
                                                                                    const idx = route.services.findIndex(t => t.tripId === trip.tripId);
                                                                                    if (idx > -1) {
                                                                                        route.services.splice(idx, 1);
                                                                                        setRoute(route.clone());
                                                                                        // If the deleted trip was selected, select the first
                                                                                        if (selectedTripId === trip.tripId && route.services.length > 0) {
                                                                                            setSelectedTripId(route.services[0].tripId);
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                onCancel={e => e?.stopPropagation()}
                                                                            >
                                                                                <Button className='icon-button btn-sm icon-only icon-12 btn-delete' icon={<Trash />} onClick={e => e.stopPropagation()} disabled={route.services.length < 2} />
                                                                            </Popconfirm>
                                                                        </div>
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                        {/* <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
                                                            <Button type="dashed" size="small" block onClick={handleAddTrip}>
                                                                + Add New Service
                                                            </Button>
                                                        </div> */}
                                                    </div>
                                                }
                                                title={<div className='d-flex align-items-center justify-content-between'>
                                                    <span>All Services</span>
                                                    <Button type="primary" className="icon-button btn-sm" icon={<PlusOutlined/>} onClick={handleAddTrip}>Add</Button>
                                                </div>}
                                                trigger="click"
                                                placement="leftTop"
                                                overlayStyle={{ width: '200px' }}
                                                onOpenChange={setServicesPopoverVisible}
                                            >
                                                <Tooltip 
                                                    placement='right'
                                                    title={anyInvalidServices && getSelectedServiceValidationMessages.length > 0 && !servicesPopoverVisible ? 
                                                        <div>{getSelectedServiceValidationMessages.map((msg, i) => <div key={i}>{msg}</div>)}</div> : 
                                                        undefined
                                                    }
                                                >
                                                    <div style={{ cursor: 'pointer', border: anyInvalidServices ? '1px solid red' : 'none', borderRadius: '7px' }}>
                                                        <TimeField
                                                            style={{ pointerEvents: useSimple ? 'none' : 'auto' }}
                                                            value={selectedTrip.getStartTime ? selectedTrip.getStartTime(showSeconds) : ''}
                                                            disabled={useSimple}
                                                            showSeconds={showSeconds}
                                                            className={"w-100 simple-input"}
                                                            onChange={(e, value) => {
                                                                if (!value) return;
                                                                value = dayjs(value, showSeconds ? 'HH:mm:ss' : 'HH:mm');
                                                                if (!value.isValid()) return;
                                                                const newStartSecs = dayjsToSecsSinceMidnight(value);
                                                                if (selectedTrip.stopTimes?.length) {
                                                                    selectedTrip.setStartTime(newStartSecs);
                                                                    // Update the trip in the route
                                                                    const tripIdx = route.services.findIndex(t => t.tripId === selectedTrip.tripId);
                                                                    route.services[tripIdx] = selectedTrip;
                                                                    setRoute(route.clone());
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </Popover>
                                            {/* <span style={{ fontSize: 12, color: '#888' }}>Trip ID: {selectedTrip.tripId}</span> */}
                                        </Col>
                                        {!useSimple && <>
                                            <Col xs={12} xl={4} className='d-flex flex-column pl-2 pr-0'>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <span className="info-heading">Calendar</span>
                                                    {hasDifferentCalendars && (
                                                        <Tooltip title="Copy this service's calendar to all others in this route">
                                                            <Popconfirm
                                                                title="Are you sure?"
                                                                onConfirm={copyCalendars}
                                                                okText="Yes"
                                                                cancelText="No"
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    type="link"
                                                                    style={{ padding: '0', fontSize: '12px', color: '#1890ff' }}
                                                                    icon={<CopyOutlined />}
                                                                />
                                                            </Popconfirm>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <CalendarSelector
                                                    obj={selectedTrip}
                                                    obsolete={selectedTrip.hasObsoleteCalendar && selectedTrip.hasObsoleteCalendar(schedulesById)}
                                                    route={route}
                                                    setRoute={setRoute}
                                                    schedules={schedules}
                                                    recurring={true}
                                                    onCalendarChange={scheduleIds => {
                                                        selectedTrip.scheduleIds = scheduleIds;
                                                        setRoute(route.clone());
                                                    }}
                                                    onDatesChange={dates => {
                                                        selectedTrip.dates = dates;
                                                        setRoute(route.clone());
                                                    }}
                                                    hidePublicCalendars
                                                />
                                            </Col>
                                            <Col xs={12} xl={4} className='d-flex flex-column pl-2 pr-0'>
                                                {/* Driver Duties summary */}
                                                <div className="d-flex" style={{gap: '6px'}}>
                                                    <span className="info-heading">Driver Duties</span>
                                                    {hasDutyOverlap &&
                                                        <AntdTooltip
                                                            title={hasDutyOverlap ? 'One or more duties have overlapping segments. Click to inspect.' : ''}
                                                            open={hasDutyOverlap ? undefined : false}
                                                        >
                                                            <Warning style={{ width: '14px', marginTop: '-2px', marginBottom: '2px' }} />
                                                        </AntdTooltip>
                                                    }
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>    
                                                    {getDriverDutiesForTrip && isTripInvalidDueToDuties && (() => {
                                                        const duties = selectedTripDuties;
                                                        return (
                                                            <>
                                                                {duties.length > 3 ? (
                                                                    <Popover
                                                                        content={
                                                                            <div style={{ maxWidth: '260px', maxHeight: '200px', overflowY: 'auto' }}>
                                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                                    {duties.map((shiftBat) => (
                                                                                        <div
                                                                                            key={shiftBat.shiftBatId}
                                                                                            className="RouteNumber-sm"
                                                                                            style={{ backgroundColor: shiftBat.shiftBatColour || '#007bff', }}
                                                                                        >
                                                                                            {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                        title="All Driver Duties"
                                                                        trigger="hover"
                                                                        placement="bottom"
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                gap: '2px',
                                                                                flexWrap: 'wrap',
                                                                            }}
                                                                            onClick={() => setImpactViewMode(v => !v)}
                                                                            className={hasDutyOverlap ||impactViewMode ? 'active' : ''}
                                                                        >
                                                                            {duties.slice(0, 3).map((shiftBat, index) => (
                                                                                <div
                                                                                    key={shiftBat.shiftBatId}
                                                                                    className="RouteNumber-sm"
                                                                                    style={{
                                                                                        minWidth: 'auto',
                                                                                        backgroundColor: shiftBat.shiftBatColour || '#007bff'
                                                                                    }}
                                                                                >
                                                                                    {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                                                </div>
                                                                            ))}
                                                                            <div
                                                                                className="RouteNumber-sm"
                                                                                style={{
                                                                                    minWidth: 'auto',
                                                                                    backgroundColor: '#E5E8E5',
                                                                                    color: '#777',
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                +{duties.length - 3} more
                                                                            </div>
                                                                        </div>
                                                                    </Popover>
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            display: 'flex',
                                                                            flexDirection: 'row',
                                                                            gap: '2px',
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                        onClick={() => setImpactViewMode(v => !v)}
                                                                        className={hasDutyOverlap ||impactViewMode ? 'active' : ''}
                                                                    >
                                                                        {duties.map((shiftBat, index) => (
                                                                            <div
                                                                                key={shiftBat.shiftBatId}
                                                                                className="RouteNumber-sm"
                                                                                style={{
                                                                                    minWidth: 'auto',
                                                                                    backgroundColor: shiftBat.shiftBatColour || '#007bff'
                                                                                }}
                                                                            >
                                                                                {shiftBat.shiftBatNumber || shiftBat.shiftBatName || 'Duty'}
                                                                            </div>
                                                                        ))}
                                                                        {duties.length === 0 && (
                                                                            <Alert message="No duties" type="error" style={{ width: '100%', padding: '4px 10px', borderRadius: '6px' }} />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </Col>
                                            <Col xs={12} xl={2} className='d-flex pl-2 align-items-center justify-content-end flex-wrap' style={{gap: '4px'}}>
                                                {/* TripDetailsModal button */}
                                                <span>
                                                    <TripDetailsModal popPlacement={'top'} route={route}
                                                        trip={selectedTrip}
                                                        operator={operator} setTrip={setTrip}
                                                        allStops={allStops} schedules={schedules}
                                                        validationHelper={validationHelper}
                                                        setRoute={setRoute} setUpdated={setUpdated}
                                                    />
                                                </span>
                                                {/* School/Regular toggle icon */}
                                                <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                                    <AntdTooltip title={selectedTrip.getRouteType() === 'School' ? 'School Service (click to toggle)' : 'Regular Service (click to toggle)'}>
                                                        <span
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                selectedTrip.routeType = selectedTrip.getRouteType() === 'School' ? 'Regular' : 'School';
                                                                // Update the trip in the route
                                                                const tripIdx = route.services.findIndex(t => t.tripId === selectedTrip.tripId);
                                                                route.services[tripIdx] = selectedTrip;
                                                                setRoute(route.clone());
                                                            }}
                                                            className={`toggle-icon ${selectedTrip.getRouteType() === 'School' ? 'school-active' : ''}`}
                                                        >
                                                            <School style={{ width: 24, height: 24, fill: selectedTrip.getRouteType() === 'School' ? '#1890ff' : '#bbb', transition: 'fill 0.2s' }} />
                                                        </span>
                                                    </AntdTooltip>
                                                </span>
                                                {/* Validation status */}
                                                {validationHelper?.global?.some(m => m.tripId === selectedTrip.tripId) && (
                                                    <Tooltip title={validationHelper.global.filter(m => m.tripId === selectedTrip.tripId).map(m => m.message).join(', ')}>
                                                        <Warning style={{ width: 16 }} />
                                                    </Tooltip>
                                                )}
                                            </Col>
                                        </>}
                                    </Row>
                                </>
                            ) : (
                                <span>No service selected</span>
                            )}
                        </div>
                        {/* Only render the selected trip */}
                        {selectedTrip && (
                            useSimple ?
                                <SimpleStopTimes
                                    charter={charter}
                                    route={route}
                                    setTrip={setTrip}
                                    buildToStop={buildToStop}
                                    showNewStop={showNewStop}
                                    showAllStops={showAllStops}
                                    editingRoute={editingRoute}
                                    setShowNewStop={setShowNewStop}
                                    showSeconds={showSeconds}
                                    cumulativeUpdate={cumulativeUpdate}
                                    setRoute={setRoute}
                                    setRouteNow={setRouteNow}
                                    operator={operator}
                                    immutable={immutable}
                                    allStops={allStops}
                                    trip={selectedTrip.clone()}
                                    setFocusStop={setFocusStop}
                                    setSelectedStop={setSelectedStop}
                                    selectedStop={selectedStop}
                                    setUpdated={setUpdated}
                                    validationHelper={validationHelper}
                                    setFocusTransfers={setFocusTransfers}
                                    focusTransfers={focusTransfers}
                                    setAllStops={setAllStops}
                                />
                                :
                                <StopTimes
                                    charter={charter}
                                    route={route}
                                    setTrip={setTrip}
                                    buildToStop={buildToStop}
                                    showNewStop={showNewStop}
                                    showAllStops={showAllStops}
                                    editingRoute={editingRoute}
                                    setShowNewStop={setShowNewStop}
                                    showSeconds={showSeconds}
                                    cumulativeUpdate={cumulativeUpdate}
                                    setRoute={setRoute}
                                    operator={operator}
                                    immutable={immutable}
                                    allStops={allStops}
                                    trip={selectedTrip.clone()}
                                    setFocusStop={setFocusStop}
                                    setSelectedStop={setSelectedStop}
                                    selectedStop={selectedStop}
                                    setUpdated={setUpdated}
                                    validationHelper={validationHelper}
                                    setFocusTransfers={setFocusTransfers}
                                    focusTransfers={focusTransfers}
                                />
                        )}

                    </Col>
                </Row>
            </div>
        </div>

    );
}

// Helper to get all validation messages for a specific trip
function getTripValidationMessages(validationHelper, trip, selectedTrip, getDriverDutiesForTrip, isTripInvalidDueToDuties) {
    if (!validationHelper) return [];
    let messages = [];
    // Only show school bell time issues if routeType is 'school'
    const isSchoolRoute = selectedTrip && typeof selectedTrip.getRouteType === 'function' && selectedTrip.getRouteType() === 'school';
    // Collect global messages for this trip
    if (Array.isArray(validationHelper.global)) {
        messages = messages.concat(
            validationHelper.global
                .filter(m => {
                    if (m.type === 'bellTime' && !isSchoolRoute) return false;
                    if (m.type === 'driverDutyMissing' || m.type === 'driverDutyOverlap') return m.tripId === trip.tripId;
                    return m.tripId === trip.tripId;
                })
                .map(m => m.msg || m.message)
        );
    }
    // Collect stop-level messages for this trip from validationHelper.stops (array of arrays)
    if (Array.isArray(validationHelper.stops)) {
        validationHelper.stops.forEach(stopMsgs => {
            if (Array.isArray(stopMsgs)) {
                stopMsgs.filter(m => m.tripId === trip.tripId).forEach(m => {
                    messages.push(m.msg || m.message);
                });
            }
        });
    }
    // --- Add direct driver duty validation here ---
    if (getDriverDutiesForTrip && isTripInvalidDueToDuties) {
        const duties = getDriverDutiesForTrip(trip.tripId);
        if (!duties.length) {
            messages.push('⛔️ No driver duty assigned to this service trip.');
        } else {
            for (const duty of duties) {
                const { overlappingRowIds } = detectDutyOverlaps(duty.rows || []);
                if (overlappingRowIds.size > 0) {
                    messages.push('⛔️ This service trip has overlapping driver duty segments.');
                    break;
                }
            }
        }
    }
    return messages;
}

export default React.memo(Trips);
